'use client';

import { createClient } from '@/lib/supabase/client';

export type DiscussionMessage = {
  id: string;
  courseId: string;
  senderId: string;
  senderName: string;
  senderRole: 'student' | 'lecturer';
  message: string;
  parentId?: string;
  createdAt: string;
};

function dbToMessage(row: any): DiscussionMessage {
  return {
    id: row.id,
    courseId: row.course_id,
    senderId: row.sender_id,
    senderName: row.sender_name,
    senderRole: row.sender_role as 'student' | 'lecturer',
    message: row.message,
    parentId: row.parent_id || undefined,
    createdAt: row.created_at,
  };
}

export const discussionService = {
  async getByCourse(courseId: string): Promise<DiscussionMessage[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('discussion_messages')
        .select('*')
        .eq('course_id', courseId)
        .order('created_at', { ascending: true });
      if (error) return [];
      return (data || []).map(dbToMessage);
    } catch {
      return [];
    }
  },

  async send(msg: Omit<DiscussionMessage, 'id' | 'createdAt'>): Promise<DiscussionMessage | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('discussion_messages')
        .insert({
          course_id: msg.courseId,
          sender_id: msg.senderId,
          sender_name: msg.senderName,
          sender_role: msg.senderRole,
          message: msg.message,
          parent_id: msg.parentId || null,
        })
        .select()
        .single();
      if (error) return null;
      return data ? dbToMessage(data) : null;
    } catch {
      return null;
    }
  },

  async delete(messageId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('discussion_messages')
        .delete()
        .eq('id', messageId);
      return !error;
    } catch {
      return false;
    }
  },

  subscribeToChannel(courseId: string, onMessage: (msg: DiscussionMessage) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel(`discussion:${courseId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'discussion_messages',
          filter: `course_id=eq.${courseId}`,
        },
        (payload) => {
          if (payload.new) onMessage(dbToMessage(payload.new));
        }
      )
      .subscribe();
    return channel;
  },
};
